import { useEffect, useState } from 'react';
import { previewDecomposition } from '../lib/api';
import { Loader2, AlertTriangle } from 'lucide-react';

const fmtNum = (v, unita) => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  const s = Number.isInteger(n) ? n.toLocaleString('it-IT') : n.toLocaleString('it-IT', { maximumFractionDigits: 2 });
  return unita ? `${s} ${unita}` : s;
};

/**
 * Mostra l'anteprima della decomposizione di quarter per un KR numerico.
 * Si attiva solo se il campi includono baseline, target_committed, scadenza e tipo='numerico'.
 *
 * onRipartizioneChange(value) viene chiamato per propagare la scelta nel campo `_ripartizione`.
 */
export default function DecompositionPreview({ campi, ripartizione, onRipartizioneChange, disabled }) {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const baseline = Number(campi?.baseline);
  const target = Number(campi?.target_committed);
  const scadenza = campi?.scadenza;
  const tipo = campi?.tipo || 'numerico';
  const unita = campi?.unita || '';
  const anno = scadenza ? parseInt(String(scadenza).slice(0, 4), 10) : null;

  const eligible =
    tipo === 'numerico' &&
    Number.isFinite(baseline) &&
    Number.isFinite(target) &&
    !!anno;

  useEffect(() => {
    if (!eligible) {
      setPeriods([]);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    previewDecomposition({
      baseline,
      target_committed: target,
      anno,
      ripartizione: ripartizione || 'lineare',
      granularita: 'quarter',
    })
      .then((res) => {
        if (alive) setPeriods(res || []);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || 'Errore preview');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [baseline, target, anno, ripartizione, eligible]);

  if (!eligible) return null;

  return (
    <div className="mt-3 border-t border-line pt-3" data-testid="decomposition-preview">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-soft">
          Decomposizione Q · {anno}
        </span>
        <select
          value={ripartizione || 'lineare'}
          onChange={(e) => onRipartizioneChange(e.target.value)}
          disabled={disabled}
          className="font-mono text-[10px] uppercase tracking-[0.15em] bg-transparent border border-line px-2 py-0.5 rounded-none focus:outline-none focus:border-ink disabled:opacity-50"
          data-testid="ripartizione-select"
        >
          <option value="lineare">lineare</option>
          <option value="a_crescere">a crescere</option>
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 font-mono text-xs text-mute">
          <Loader2 size={10} strokeWidth={1.5} className="animate-spin" />
          Calcolo…
        </div>
      )}

      {error && (
        <div className="font-mono text-xs text-accent"><AlertTriangle className="inline h-3 w-3 align-[-2px]" strokeWidth={1.5} /> {error}</div>
      )}

      {!loading && !error && periods.length === 4 && (
        <div className="grid grid-cols-4 gap-px bg-line border border-line">
          {periods.map((p) => (
            <div key={p.etichetta} className="bg-paper px-2 py-2" data-testid="decomp-period">
              <div className="font-mono text-[10px] text-mute mb-0.5">{p.etichetta}</div>
              <div className="font-mono text-xs text-ink tabular-nums">
                {fmtNum(p.suggerito, unita)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 font-mono text-[10px] text-mute leading-relaxed">
        Baseline {fmtNum(baseline, unita)} → Target {fmtNum(target, unita)}. Salvato come <span className="text-ink">target_periodo</span> alla conferma.
      </div>
    </div>
  );
}
